import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';

const POLL_MS = 2000;
const MAX_POLLS = 30;

const PaymentResult = ({ cancelled = false }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');
  const [state, setState] = useState(cancelled ? 'cancelled' : 'checking');
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    try {
      const res = await api.get(`/api/payments/status/${sessionId}`);
      if (res.data.payment_status === 'paid') { setState('paid'); return true; }
      if (['failed', 'expired'].includes(res.data.payment_status)) { setState('failed'); return true; }
    } catch (err) {
      setState('failed');
      return true;
    }
    return false;
  }, [sessionId]);

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!sessionId && !cancelled) setState('failed');
      return;
    }
    let timer;
    const run = async () => {
      const done = await poll();
      if (!done) {
        setAttempts(a => {
          const next = a + 1;
          if (next >= MAX_POLLS) setState('timeout');
          return next;
        });
        timer = setTimeout(run, POLL_MS);
      }
    };
    run();
    return () => clearTimeout(timer);
  }, [cancelled, sessionId, poll]);

  const copy = {
    checking: { icon: <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />, title: 'Confirming your payment…',
      body: `Checking with Stripe${attempts > 3 ? ' — this can take a few seconds' : ''}.` },
    paid: { icon: <CheckCircle2 className="w-10 h-10 text-emerald-500" />, title: 'You\'re on the new plan',
      body: 'Your payment went through and your plan is active. Stripe has emailed your receipt.' },
    failed: { icon: <XCircle className="w-10 h-10 text-red-500" />, title: 'That payment didn\'t complete',
      body: 'No plan change was made and you have not been charged. Try again, or use a different card.' },
    timeout: { icon: <Loader2 className="w-10 h-10 text-amber-500" />, title: 'Still confirming',
      body: 'Stripe is taking longer than usual. Your plan will update automatically once it settles — no need to pay again.' },
    cancelled: { icon: <XCircle className="w-10 h-10 text-slate-400" />, title: 'Checkout cancelled',
      body: 'Nothing was charged and your plan is unchanged.' },
  }[state];

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: '#07090E' }}>
      <div className="w-full max-w-md bg-slate-900/80 border border-slate-800 rounded-2xl p-8 text-center"
        data-testid={`payment-result-${state}`}>
        <div className="flex justify-center mb-5">{copy.icon}</div>
        <h1 className="text-xl font-bold text-slate-100">{copy.title}</h1>
        <p className="text-sm text-slate-400 mt-3 leading-relaxed">{copy.body}</p>
        <button onClick={() => navigate('/dashboard')} data-testid="payment-result-dashboard-btn"
          className="mt-7 w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold transition-colors">
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

export default PaymentResult;
